import { CheckCircledIcon } from "@radix-ui/react-icons";
import { Box, Button, Card, Container, Flex, Heading, Text, TextField } from "@radix-ui/themes";
import { type FormEvent, useEffect, useState } from "react";
import logo from "../../assets/logo.png";
import { useAuth } from "../../contexts/AuthContext";
import Alert from "../shared/Alert";
import Footer from "../shared/Footer";

/**
 * Completes password recovery, refreshes the authenticated browser session, and lets the user
 * choose the Agent or MCP product without redirecting them to either product automatically.
 */
const ResetPasswordView = () => {
    const { refresh } = useAuth();
    const [token] = useState(() => new URLSearchParams(window.location.hash.substring(1)).get("token") || "");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [loading, setLoading] = useState(false);
    const [signedInEmail, setSignedInEmail] = useState("");
    const [msg, setMsg] = useState({
        type: "",
        text: "",
    });

    useEffect(() => {
        if (!token) {
            setMsg({
                type: "error",
                text: "Invalid or expired password reset link. Please request a new one.",
            });
            return;
        }
    }, [
        token,
    ]);

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setMsg({
            type: "",
            text: "",
        });

        if (!token) {
            setMsg({
                type: "error",
                text: "Invalid or expired password reset link. Please request a new one.",
            });
            setLoading(false);
            return;
        }

        if (password !== confirmPassword) {
            setMsg({
                type: "error",
                text: "Passwords do not match",
            });
            setLoading(false);
            return;
        }

        if (password.length < 6) {
            setMsg({
                type: "error",
                text: "Password must be at least 6 characters",
            });
            setLoading(false);
            return;
        }

        try {
            const response = await fetch("/auth/backend/reset-password-session", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify({
                    token,
                    password,
                }),
            });

            const resData = await response.json();

            if (!response.ok) {
                throw new Error(resData.error_description || resData.error || "Failed to update password");
            }

            window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}`);
            await refresh();
            setSignedInEmail(resData.user.email);
        } catch (err: any) {
            console.error("Failed to update password", err);
            setMsg({
                type: "error",
                text: err.message || "Failed to update password. Please try again.",
            });
        } finally {
            setLoading(false);
        }
    };

    if (signedInEmail) {
        return (
            <Container
                size="2"
                p="4"
                style={{
                    minWidth: "350px",
                }}
            >
                <Card size="4">
                    <Flex
                        direction="column"
                        gap="4"
                        align="center"
                        style={{
                            textAlign: "center",
                        }}
                    >
                        <CheckCircledIcon width="64" height="64" color="var(--accent-9)" />
                        <Heading size="6">Password changed successfully</Heading>
                        <Text color="gray" size="3">
                            You are signed in as <strong>{signedInEmail}</strong>.
                        </Text>
                        <Text color="gray" size="2">
                            Choose which Desktop Commander app you want to open:
                        </Text>
                        <Flex direction="column" gap="3">
                            <Button asChild size="3">
                                <a href={import.meta.env.VITE_AGENT_URL} target="_blank" rel="noopener noreferrer">
                                    SHARED AGENT
                                </a>
                            </Button>
                            <Button asChild size="3">
                                <a href={import.meta.env.VITE_MCP_URL} target="_blank" rel="noopener noreferrer">
                                    REMOTE MCP
                                </a>
                            </Button>
                        </Flex>
                    </Flex>
                </Card>
                <Footer />
            </Container>
        );
    }

    return (
        <Container
            size="2"
            p="4"
            style={{
                minWidth: "350px",
            }}
        >
            <Card size="4">
                <Flex
                    direction="column"
                    gap="4"
                    align="center"
                    style={{
                        textAlign: "center",
                        marginBottom: "24px",
                    }}
                >
                    <img
                        src={logo}
                        alt="Logo"
                        style={{
                            width: 64,
                            height: 64,
                            objectFit: "contain",
                        }}
                    />
                    <Heading size="6">Set New Password</Heading>
                    <Text color="gray" size="2">
                        Enter your new password below
                    </Text>
                </Flex>

                <form onSubmit={handleSubmit}>
                    <Flex direction="column" gap="4">
                        {msg.text && <Alert type={msg.type as "error" | "success"} message={msg.text} />}

                        <Box>
                            <Text
                                as="label"
                                size="2"
                                weight="bold"
                                mb="1"
                                style={{
                                    display: "block",
                                }}
                            >
                                New Password
                            </Text>
                            <TextField.Root
                                placeholder="New password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                type="password"
                                required
                            />
                        </Box>

                        <Box>
                            <Text
                                as="label"
                                size="2"
                                weight="bold"
                                mb="1"
                                style={{
                                    display: "block",
                                }}
                            >
                                Confirm Password
                            </Text>
                            <TextField.Root
                                placeholder="Confirm new password"
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                type="password"
                                required
                            />
                        </Box>

                        <Button
                            type="submit"
                            loading={loading}
                            style={{
                                cursor: "pointer",
                            }}
                        >
                            Update Password
                        </Button>
                    </Flex>
                </form>
            </Card>
            <Footer />
        </Container>
    );
};

export default ResetPasswordView;
